import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, IndianRupee, CreditCard, Download, Search, X, AlertTriangle, MessageCircle, Printer, History, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { downloadXlsx } from "@/lib/export";
import { PaginationBar } from "@/components/PaginationBar";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const now = new Date();
const YEARS = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

const emptyFee = {
  resident_id: "", resident_name: "",
  fee_type: "hostel", amount: "",
  month: now.toLocaleString("en", { month: "long" }),
  year: String(now.getFullYear()),
  due_date: "", force: false,
  // v12 — subscription coverage (separate from amount)
  start_date: new Date().toISOString().slice(0, 10),
  duration_type: "1month",
  duration_days: "",
  end_date: "",
};

const DURATION_OPTIONS = [
  { v: "1day", l: "1 Day", days: 1 },
  { v: "3day", l: "3 Days", days: 3 },
  { v: "7day", l: "7 Days", days: 7 },
  { v: "15day", l: "15 Days", days: 15 },
  { v: "30day", l: "30 Days", days: 30 },
  { v: "1month", l: "1 Month", days: null },
  { v: "2month", l: "2 Months", days: null },
  { v: "3month", l: "3 Months", days: null },
  { v: "6month", l: "6 Months", days: null },
  { v: "1year", l: "1 Year", days: null },
  { v: "custom", l: "Custom", days: null },
];

function computeEndDate(startISO, durationType, customDays) {
  if (!startISO || !durationType) return "";
  const opt = DURATION_OPTIONS.find((o) => o.v === durationType);
  if (!opt) return "";
  const start = new Date(startISO);
  if (isNaN(start.getTime())) return "";
  if (durationType === "custom") {
    const d = parseInt(customDays, 10);
    if (!d || d <= 0) return "";
    const end = new Date(start); end.setDate(end.getDate() + d - 1);
    return end.toISOString().slice(0, 10);
  }
  if (opt.days) {
    const end = new Date(start); end.setDate(end.getDate() + opt.days - 1);
    return end.toISOString().slice(0, 10);
  }
  // month / year (calendar)
  const monthMap = { "1month": 1, "2month": 2, "3month": 3, "6month": 6, "1year": 12 };
  const m = monthMap[durationType];
  if (!m) return "";
  const end = new Date(start); end.setMonth(end.getMonth() + m); end.setDate(end.getDate() - 1);
  return end.toISOString().slice(0, 10);
}

export default function Fees() {
  const { user } = useAuth();
  const [fees, setFees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [residents, setResidents] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  // Student search state (inside "New Fee" dialog)
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [dupWarning, setDupWarning] = useState(false);

  const canEdit = user?.role === "admin" || user?.role === "accountant" || user?.role === "warden" || user?.role === "hostel_manager";

  const [form, setForm] = useState(emptyFee);
  const [pay, setPay] = useState({
    amount: "", mode: "upi", reference: "",
    // v30 — Actual Payment Received Date (default today, but user can back-date).
    payment_date: new Date().toISOString().slice(0, 10),
    // Split-mode state
    split: false,
    cash_amount: "", online_amount: "", online_mode: "upi", online_reference: "",
  });

  // Payment history dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (fee) => {
    setHistoryOpen(true); setHistoryData(null); setHistoryLoading(true);
    try {
      const { data } = await api.get(`/fees/${fee.id}/history`);
      setHistoryData(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setHistoryOpen(false);
    } finally { setHistoryLoading(false); }
  };

  const refundPayment = async (paymentId, invoiceNo) => {
    const reason = window.prompt(`Refund this payment on ${invoiceNo}? Enter reason:`);
    if (!reason) return;
    try {
      await api.post(`/payments/${paymentId}/reverse`, { reason });
      toast.success("Payment refunded");
      // refresh history + main list
      if (historyData) {
        const { data } = await api.get(`/fees/${historyData.fee_id}/history`);
        setHistoryData(data);
      }
      refreshAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = async () => {
    // Residents list stays cached (used to resolve names). Fees/payments now paginate.
    try {
      const { data } = await api.get("/residents");
      setResidents(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load().catch(() => {}); }, []);

  // v28 — Server-side paginated fees state
  const [feePage, setFeePage] = useState(1);
  const [feeLimit, setFeeLimit] = useState(25);
  const [feeTotal, setFeeTotal] = useState(0);
  const [feePages, setFeePages] = useState(1);
  const [feeQ, setFeeQ] = useState("");
  const [feeStatus, setFeeStatus] = useState("all");
  const [feeModule, setFeeModule] = useState("all");
  const [feesLoading, setFeesLoading] = useState(false);

  const loadFees = async () => {
    setFeesLoading(true);
    try {
      const params = new URLSearchParams({ page: String(feePage), limit: String(feeLimit) });
      if (feeQ) params.set("q", feeQ);
      if (feeStatus !== "all") params.set("status", feeStatus);
      if (feeModule !== "all") params.set("module", feeModule);
      const { data } = await api.get(`/fees/paginated?${params}`);
      setFees(data.items || []);
      setFeeTotal(data.total || 0);
      setFeePages(data.total_pages || 1);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setFeesLoading(false); }
  };
  useEffect(() => { loadFees(); /* eslint-disable-next-line */ }, [feePage, feeLimit, feeQ, feeStatus, feeModule]);
  useEffect(() => { setFeePage(1); }, [feeQ, feeStatus, feeModule]);

  // v28 — Server-side paginated payments state
  const [payListPage, setPayListPage] = useState(1);
  const [payListLimit, setPayListLimit] = useState(25);
  const [payListTotal, setPayListTotal] = useState(0);
  const [payListPages, setPayListPages] = useState(1);
  const [payListQ, setPayListQ] = useState("");
  const [payListMode, setPayListMode] = useState("all");
  const [payListFrom, setPayListFrom] = useState("");
  const [payListTo, setPayListTo] = useState("");
  const [paysLoading, setPaysLoading] = useState(false);

  const loadPays = async () => {
    setPaysLoading(true);
    try {
      const params = new URLSearchParams({ page: String(payListPage), limit: String(payListLimit) });
      if (payListQ) params.set("q", payListQ);
      if (payListMode !== "all") params.set("mode", payListMode);
      if (payListFrom) params.set("from_date", payListFrom);
      if (payListTo) params.set("to_date", payListTo);
      const { data } = await api.get(`/payments/paginated?${params}`);
      setPayments(data.items || []);
      setPayListTotal(data.total || 0);
      setPayListPages(data.total_pages || 1);
    } catch (err) { /* silent */ }
    finally { setPaysLoading(false); }
  };
  useEffect(() => { loadPays(); /* eslint-disable-next-line */ }, [payListPage, payListLimit, payListQ, payListMode, payListFrom, payListTo]);
  useEffect(() => { setPayListPage(1); }, [payListQ, payListMode, payListFrom, payListTo]);

  const refreshAll = () => { load(); loadFees(); loadPays(); };

  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/students/search?q=${encodeURIComponent(searchQ)}`)
        .then((r) => setSearchResults(r.data))
        .catch(() => setSearchResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [searchQ]);

  const period = `${form.month} ${form.year}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.resident_id) { toast.error("Select a resident first"); return; }
    if (!form.start_date) { toast.error("Coverage start date is required"); return; }
    if (form.duration_type === "custom" && (!form.duration_days || Number(form.duration_days) <= 0)) {
      toast.error("Enter number of days for custom duration"); return;
    }
    const auto_end = computeEndDate(form.start_date, form.duration_type, form.duration_days);
    try {
      await api.post("/fees", {
        resident_id: form.resident_id, fee_type: form.fee_type,
        amount: parseFloat(form.amount), period,
        due_date: form.due_date || null, force: form.force,
        start_date: form.start_date,
        end_date: auto_end || null,
        duration_type: form.duration_type,
        duration_days: form.duration_type === "custom" ? parseInt(form.duration_days, 10) : null,
      });
      toast.success(`Fee created — covers ${form.start_date} to ${auto_end}`);
      setOpen(false);
      setForm(emptyFee);
      setSearchQ(""); setSearchResults([]); setDupWarning(false);
      refreshAll();
    } catch (err) {
      if (err?.response?.status === 409) {
        setDupWarning(true);
        toast.error("Duplicate — same period already billed. Toggle 'force' to override.");
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  const submitPay = async (e) => {
    e.preventDefault();
    if (!pay.payment_date) { toast.error("Payment Received Date is required"); return; }
    try {
      if (pay.split) {
        const cash = parseFloat(pay.cash_amount || 0);
        const online = parseFloat(pay.online_amount || 0);
        const outstanding = Number(payFor.amount) - Number(payFor.paid_amount || 0);
        if (cash < 0 || online < 0) { toast.error("Amounts cannot be negative"); return; }
        if (cash === 0 && online === 0) { toast.error("Enter at least one amount"); return; }
        if (cash + online > outstanding + 0.001) {
          toast.error(`Total ₹${(cash+online).toLocaleString("en-IN")} exceeds outstanding ₹${outstanding.toLocaleString("en-IN")}`);
          return;
        }
        if (cash > 0) {
          await api.post("/payments", { fee_id: payFor.id, amount: cash,
            mode: "cash", payment_date: pay.payment_date });
        }
        if (online > 0) {
          await api.post("/payments", {
            fee_id: payFor.id, amount: online,
            mode: pay.online_mode, reference: pay.online_reference,
            payment_date: pay.payment_date,
          });
        }
        toast.success(`Split payment recorded on ${pay.payment_date} — Cash ₹${cash.toLocaleString("en-IN")} + ${pay.online_mode.toUpperCase()} ₹${online.toLocaleString("en-IN")}`);
      } else {
        await api.post("/payments", {
          fee_id: payFor.id, amount: parseFloat(pay.amount),
          mode: pay.mode, reference: pay.reference,
          payment_date: pay.payment_date,
        });
        toast.success(`Payment recorded on ${pay.payment_date}`);
      }
      setPayOpen(false);
      setPay({ amount: "", mode: "upi", reference: "",
               payment_date: new Date().toISOString().slice(0, 10),
               split: false,
               cash_amount: "", online_amount: "", online_mode: "upi", online_reference: "" });
      refreshAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const payOnline = async (fee) => {
    setCheckoutLoading(fee.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        fee_id: fee.id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err)); setCheckoutLoading(null); }
  };

  const fetchReceiptBlob = async (paymentId) => {
    const token = localStorage.getItem("hostel_token");
    const res = await fetch(`${API}/payments/${paymentId}/receipt`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include",
    });
    if (!res.ok) throw new Error("Receipt fetch failed");
    return res.blob();
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const blob = await fetchReceiptBlob(paymentId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `receipt-${paymentId.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download receipt"); }
  };

  const printReceipt = async (paymentId) => {
    try {
      const blob = await fetchReceiptBlob(paymentId);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (win) {
        win.addEventListener("load", () => {
          try { win.print(); }
          catch (e) { console.error("Print dialog failed", e); }
        });
      }
    } catch (err) {
      console.error("printReceipt failed", err);
      toast.error("Could not open receipt for printing");
    }
  };

  const shareOnWhatsApp = (payment) => {
    const resident = residents.find((r) => r.id === payment.resident_id);
    const phone = (resident?.phone || "").replace(/\D/g, "");
    const amount = Number(payment.amount).toLocaleString("en-IN");
    const msg = `Hi ${resident?.name || ""}, your payment receipt of ₹${amount} (${payment.mode?.toUpperCase()}) has been recorded on ${new Date(payment.created_at).toLocaleDateString()}. Reference: ${payment.reference || payment.id.slice(0, 8)}. Thank you!`;
    const encoded = encodeURIComponent(msg);
    const url = phone ? `https://wa.me/${phone}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const resName = (id) => residents.find((r) => r.id === id)?.name || "—";

  const pickResident = async (r) => {
    setForm({ ...form, resident_id: r.id, resident_name: r.name });
    setSearchQ(""); setSearchResults([]); setDupWarning(false);
    await prefillSubStatus(r.id, form.fee_type);
  };

  // v18.1 — Prefill Start Date + Amount from resident's last paid_through + outstanding
  const [subStatus, setSubStatus] = useState(null);
  // v19.1 — Track the last auto-filled amount so we don't clobber a manually-typed value
  const [lastAutoAmount, setLastAutoAmount] = useState("");
  const prefillSubStatus = async (residentId, feeType) => {
    if (!residentId || !["hostel", "mess"].includes(feeType)) {
      setSubStatus(null);
      return;
    }
    const module = feeType === "hostel" ? "residence" : "mess";
    try {
      const { data } = await api.get(`/residents/${residentId}/subscription-status?module=${module}`);
      setSubStatus(data);
      setForm((f) => {
        // Overwrite Amount only if it's empty OR still equals the previous auto-value
        const userDirty = f.amount !== "" && f.amount !== lastAutoAmount;
        const nextAmount = data.outstanding > 0 && !userDirty
          ? String(data.outstanding)
          : f.amount;
        return {
          ...f,
          start_date: data.next_start || f.start_date,
          amount: nextAmount,
        };
      });
      setLastAutoAmount(data.outstanding > 0 ? String(data.outstanding) : "");
    } catch { setSubStatus(null); }
  };

  const onFeeTypeChange = (v) => {
    setForm((f) => ({ ...f, fee_type: v }));
    if (form.resident_id) prefillSubStatus(form.resident_id, v);
  };

  return (
    <div className="space-y-6" data-testid="fees-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Finance</div>
          <h1 className="font-display text-4xl font-bold mt-1">Fees & Payments</h1>
          <p className="text-muted-foreground mt-1">Track dues, collect online or record manually, download receipts.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadXlsx("fees").catch(() => toast.error("Export failed"))}
                  data-testid="export-fees">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
          {canEdit && (
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyFee); setSearchQ(""); setDupWarning(false); setSubStatus(null); setLastAutoAmount(""); } }}>
              <DialogTrigger asChild>
                <Button data-testid="add-fee-button"><Plus className="w-4 h-4 mr-2" /> New Fee</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-display text-2xl">Create Fee Receipt</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Student *</Label>
                    {form.resident_id ? (
                      <div className="flex items-center gap-2 p-3 border border-border rounded-md bg-secondary/40" data-testid="selected-resident">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{form.resident_name}</div>
                        </div>
                        <Button size="sm" variant="ghost"
                          onClick={() => setForm({ ...form, resident_id: "", resident_name: "" })}
                          data-testid="clear-resident">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <Input className="pl-9"
                            placeholder="Search by name, mobile, student ID, room..."
                            value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                            data-testid="fee-student-search" />
                        </div>
                        {searchResults.length > 0 && (
                          <div className="mt-1 max-h-56 overflow-y-auto border border-border rounded-md">
                            {searchResults.map((r) => (
                              <button key={r.id} type="button" onClick={() => pickResident(r)}
                                className="w-full text-left px-3 py-2 hover:bg-secondary transition-colors"
                                data-testid={`search-result-${r.id}`}>
                                <div className="font-medium text-sm">{r.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {[r.phone, r.room_no && `Room ${r.room_no}`, r.company]
                                    .filter(Boolean).join(" • ")}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        {searchQ && searchResults.length === 0 && (
                          <div className="text-xs text-muted-foreground mt-1 px-1">No matches for "{searchQ}"</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Fee Type</Label>
                      <Select value={form.fee_type} onValueChange={onFeeTypeChange}>
                        <SelectTrigger data-testid="fee-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hostel">Hostel</SelectItem>
                          <SelectItem value="mess">Mess</SelectItem>
                          <SelectItem value="deposit">Deposit</SelectItem>
                          <SelectItem value="penalty">Penalty</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount (₹) *</Label>
                      <Input required type="number" step="0.01" value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        data-testid="fee-amount" />
                      {subStatus && subStatus.outstanding > 0 && (
                        <div className="text-[11px] rounded border border-destructive/30 bg-destructive/5 px-2 py-1.5" data-testid="fee-outstanding-hint">
                          <div className="flex items-center gap-1 text-destructive font-semibold">
                            <AlertTriangle className="w-3 h-3" />
                            Outstanding {form.fee_type === "hostel" ? "hostel" : "mess"} balance: ₹{Number(subStatus.outstanding).toLocaleString("en-IN")}
                          </div>
                          {subStatus.outstanding_expired > 0 && (
                            <div className="text-muted-foreground mt-0.5">
                              …of which <span className="font-semibold text-destructive">₹{Number(subStatus.outstanding_expired).toLocaleString("en-IN")}</span> is from expired subscriptions.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Select value={form.month} onValueChange={(v) => { setForm({ ...form, month: v, force: false }); setDupWarning(false); }}>
                        <SelectTrigger data-testid="fee-month"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Select value={form.year} onValueChange={(v) => { setForm({ ...form, year: v, force: false }); setDupWarning(false); }}>
                        <SelectTrigger data-testid="fee-year"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* v12 — Subscription coverage (SEPARATE from amount) */}
                  <div className="p-3 rounded-md border border-primary/20 bg-primary/5 space-y-3">
                    <div className="eyebrow text-muted-foreground">Coverage window (independent of amount)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Start Date *</Label>
                        <Input required type="date" value={form.start_date}
                          onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                          data-testid="fee-start-date" />
                      </div>
                      <div className="space-y-2">
                        <Label>Duration *</Label>
                        <Select value={form.duration_type} onValueChange={(v) => setForm({ ...form, duration_type: v })}>
                          <SelectTrigger data-testid="fee-duration"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DURATION_OPTIONS.map((o) => (
                              <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {form.duration_type === "custom" && (
                      <div className="space-y-2">
                        <Label>Number of Days *</Label>
                        <Input required type="number" min="1" value={form.duration_days}
                          onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                          data-testid="fee-duration-days" placeholder="e.g. 45" />
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground border-t border-primary/20 pt-2 mt-2">
                      Coverage ends: <span className="font-mono font-semibold text-foreground" data-testid="fee-end-date">
                        {computeEndDate(form.start_date, form.duration_type, form.duration_days) || "—"}
                      </span>
                      {subStatus?.paid_through && (
                        <div className="mt-1 text-[11px]" data-testid="fee-continues-from">
                          Continues from previous paid-through: <span className="font-mono font-semibold text-foreground">{subStatus.paid_through}</span>
                        </div>
                      )}
                      <div className="mt-1 text-[11px]">
                        Note: subscription duration is independent of amount. Enter the amount separately.
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input type="date" value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                      data-testid="fee-due" />
                  </div>

                  {dupWarning && (
                    <div className="p-3 border border-destructive/40 bg-destructive/10 rounded-md" data-testid="dup-warning">
                      <div className="flex items-start gap-2 text-sm">
                        <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive" />
                        <div className="flex-1">
                          <div className="font-medium">Duplicate fee period</div>
                          <div className="text-muted-foreground mt-1">A {form.fee_type} fee for <span className="font-mono">{period}</span> already exists for this student.</div>
                          <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                            <input type="checkbox" checked={form.force}
                              onChange={(e) => setForm({ ...form, force: e.target.checked })}
                              data-testid="fee-force" />
                            <span className="text-xs">Yes, add another anyway (override)</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  <DialogFooter>
                    <Button type="submit" data-testid="fee-save">Create Fee</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card className="p-4 border-border bg-white">
        <div className="eyebrow text-muted-foreground mb-3">Outstanding & recent fees</div>
        {/* v28 — Fee filter bar (module / status / search) */}
        <div className="flex items-center gap-3 flex-wrap mb-3" data-testid="fees-filters">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={feeQ} onChange={(e) => setFeeQ(e.target.value)}
              placeholder="Search resident / invoice / type…" className="pl-9 h-9"
              data-testid="fees-search" />
          </div>
          <Select value={feeModule} onValueChange={setFeeModule}>
            <SelectTrigger className="w-[150px] h-9" data-testid="fees-module">
              <SelectValue placeholder="Module" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modules</SelectItem>
              <SelectItem value="residence">Residence</SelectItem>
              <SelectItem value="mess">Mess</SelectItem>
            </SelectContent>
          </Select>
          <Select value={feeStatus} onValueChange={setFeeStatus}>
            <SelectTrigger className="w-[170px] h-9" data-testid="fees-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partially_paid">Partially Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          {feesLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fees-table">
            <thead>
              <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                <th className="py-3 pr-4">Invoice</th>
                <th className="py-3 pr-4">Resident</th>
                <th className="py-3 pr-4">Type</th>
                <th className="py-3 pr-4">Period</th>
                <th className="py-3 pr-4 text-right">Amount</th>
                <th className="py-3 pr-4 text-right">Paid</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {fees.map((f) => (
                <tr key={f.id} className="border-b border-border/50 hover:bg-secondary/40">
                  <td className="py-3 pr-4 font-mono text-xs">{f.invoice_no || "—"}</td>
                  <td className="py-3 pr-4 font-medium">{resName(f.resident_id)}</td>
                  <td className="py-3 pr-4 capitalize text-muted-foreground">{f.fee_type}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{f.period || "—"}</td>
                  <td className="py-3 pr-4 text-right font-mono">₹{Number(f.amount).toLocaleString("en-IN")}</td>
                  <td className="py-3 pr-4 text-right font-mono">₹{Number(f.paid_amount || 0).toLocaleString("en-IN")}</td>
                  <td className="py-3 pr-4">
                    <Badge variant={f.status === "paid" ? "default" : f.status === "overdue" ? "destructive" : "secondary"} className="capitalize">
                      {f.status}
                    </Badge>
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <div className="flex gap-2 justify-end flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => openHistory(f)}
                        data-testid={`history-${f.id}`} title="Payment history">
                        <History className="w-3 h-3 mr-1" /> History
                      </Button>
                      {f.status !== "paid" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => payOnline(f)}
                            disabled={checkoutLoading === f.id} data-testid={`pay-online-${f.id}`}>
                            <CreditCard className="w-3 h-3 mr-1" />
                            {checkoutLoading === f.id ? "..." : "Pay Online"}
                          </Button>
                          {canEdit && (
                            <Button size="sm" variant="outline"
                              onClick={() => { setPayFor(f); setPay({ ...pay, amount: String(Number(f.amount) - Number(f.paid_amount || 0)) }); setPayOpen(true); }}
                              data-testid={`record-payment-${f.id}`}>
                              <IndianRupee className="w-3 h-3 mr-1" /> Record
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {fees.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">No fees match the filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar page={feePage} total={feeTotal} limit={feeLimit} totalPages={feePages}
          onPageChange={setFeePage} onLimitChange={(l) => { setFeeLimit(l); setFeePage(1); }} />
      </Card>

      {payListTotal > 0 && (
        <Card className="p-4 border-border bg-white" data-testid="payments-card">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow text-muted-foreground">Payments log</div>
            <Button variant="outline" size="sm" onClick={() => downloadXlsx("payments").catch(() => toast.error("Export failed"))}
                    data-testid="export-payments">
              <Download className="w-3 h-3 mr-1" /> Excel
            </Button>
          </div>
          {/* v28 — Payment filter bar */}
          <div className="flex items-center gap-3 flex-wrap mb-3" data-testid="payments-filters">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={payListQ} onChange={(e) => setPayListQ(e.target.value)}
                placeholder="Search receipt / reference…" className="pl-9 h-9"
                data-testid="payments-search" />
            </div>
            <Select value={payListMode} onValueChange={setPayListMode}>
              <SelectTrigger className="w-[150px] h-9" data-testid="payments-mode">
                <SelectValue placeholder="Mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="razorpay">Razorpay</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={payListFrom} onChange={(e) => setPayListFrom(e.target.value)}
              className="h-9 w-[150px]" title="From date" data-testid="payments-from" />
            <Input type="date" value={payListTo} onChange={(e) => setPayListTo(e.target.value)}
              className="h-9 w-[150px]" title="To date" data-testid="payments-to" />
            {(payListFrom || payListTo || payListMode !== "all" || payListQ) && (
              <Button variant="ghost" size="sm" className="h-9"
                onClick={() => { setPayListFrom(""); setPayListTo(""); setPayListMode("all"); setPayListQ(""); }}
                data-testid="payments-clear">Clear</Button>
            )}
            {paysLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead>
                <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                  <th className="py-3 pr-4">Resident</th>
                  <th className="py-3 pr-4">Mode</th>
                  <th className="py-3 pr-4">Reference</th>
                  <th className="py-3 pr-4">When</th>
                  <th className="py-3 pr-4 text-right">Amount</th>
                  <th className="py-3 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-border/50">
                    <td className="py-3 pr-4 font-medium">{resName(p.resident_id) || p.resident_name || "—"}</td>
                    <td className="py-3 pr-4 uppercase text-xs">{p.mode?.replace("_", " ")}</td>
                    <td className="py-3 pr-4 font-mono text-xs text-muted-foreground truncate max-w-[240px]">{p.reference || "—"}</td>
                    <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
                    <td className="py-3 pr-4 text-right font-mono font-semibold">₹{Number(p.amount).toLocaleString("en-IN")}</td>
                    <td className="py-3 pr-4 text-right">
                      <div className="flex gap-1 justify-end flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => downloadReceipt(p.id)}
                                data-testid={`download-receipt-${p.id}`} title="Download PDF">
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => printReceipt(p.id)}
                                data-testid={`print-receipt-${p.id}`} title="Print">
                          <Printer className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => shareOnWhatsApp(p)}
                                data-testid={`whatsapp-receipt-${p.id}`} title="Share on WhatsApp"
                                className="text-green-700 hover:text-green-800">
                          <MessageCircle className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-muted-foreground text-xs">
                    No payments match the filters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar page={payListPage} total={payListTotal} limit={payListLimit} totalPages={payListPages}
            onPageChange={setPayListPage} onLimitChange={(l) => { setPayListLimit(l); setPayListPage(1); }} />
        </Card>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Record Payment</DialogTitle></DialogHeader>
          {payFor && (
            <form onSubmit={submitPay} className="space-y-4">
              <div className="text-sm text-muted-foreground">
                For <span className="font-medium text-foreground">{resName(payFor.resident_id)}</span> —
                {" "}Invoice <span className="font-mono">{payFor.invoice_no || "—"}</span> •
                {" "}Fee ₹{Number(payFor.amount).toLocaleString("en-IN")} •
                {" "}outstanding <span className="font-semibold text-primary">₹{(Number(payFor.amount) - Number(payFor.paid_amount || 0)).toLocaleString("en-IN")}</span>
              </div>

              {/* v14 — Single / Split toggle */}
              <div className="flex gap-2" data-testid="pay-mode-toggle">
                <Button type="button" size="sm"
                  variant={!pay.split ? "default" : "outline"}
                  onClick={() => setPay({ ...pay, split: false })}
                  data-testid="pay-toggle-single">Single Payment</Button>
                <Button type="button" size="sm"
                  variant={pay.split ? "default" : "outline"}
                  onClick={() => setPay({ ...pay, split: true })}
                  data-testid="pay-toggle-split">Split Payment (Cash + Online)</Button>
              </div>

              {/* v30 — Payment Received Date (required). Backdate to record cash
                   collected days ago into the correct dashboard/report day. */}
              <div className="space-y-2">
                <Label>Payment Received Date * <span className="text-xs text-muted-foreground">(actual date money was received — not today unless picked)</span></Label>
                <Input type="date" required value={pay.payment_date}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setPay({ ...pay, payment_date: e.target.value })}
                  data-testid="payment-received-date" />
              </div>

              {!pay.split ? (
                <>
                  <div className="space-y-2">
                    <Label>Amount *</Label>
                    <Input type="number" step="0.01" required min="0.01"
                      max={Number(payFor.amount) - Number(payFor.paid_amount || 0)}
                      value={pay.amount}
                      onChange={(e) => setPay({ ...pay, amount: e.target.value })}
                      data-testid="payment-amount" />
                    <p className="text-xs text-muted-foreground">
                      Partial payments allowed. Max: ₹{(Number(payFor.amount) - Number(payFor.paid_amount || 0)).toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Mode</Label>
                    <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}>
                      <SelectTrigger data-testid="payment-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash (→ Cash Book)</SelectItem>
                        <SelectItem value="upi">UPI (→ Bank Book)</SelectItem>
                        <SelectItem value="card">Card (→ Bank Book)</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer (→ Bank Book)</SelectItem>
                        <SelectItem value="razorpay">Razorpay (→ Bank Book)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Reference / Transaction ID</Label>
                    <Input value={pay.reference}
                      onChange={(e) => setPay({ ...pay, reference: e.target.value })}
                      placeholder="UTR / UPI ref / pay_xxx / cheque no."
                      data-testid="payment-reference" />
                    <p className="text-xs text-muted-foreground">Duplicate references are blocked automatically.</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Cash Amount</Label>
                      <Input type="number" step="0.01" min="0" value={pay.cash_amount}
                        onChange={(e) => setPay({ ...pay, cash_amount: e.target.value })}
                        placeholder="0.00" data-testid="split-cash-amount" />
                    </div>
                    <div className="space-y-2">
                      <Label>Online / UPI Amount</Label>
                      <Input type="number" step="0.01" min="0" value={pay.online_amount}
                        onChange={(e) => setPay({ ...pay, online_amount: e.target.value })}
                        placeholder="0.00" data-testid="split-online-amount" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Online Mode</Label>
                    <Select value={pay.online_mode} onValueChange={(v) => setPay({ ...pay, online_mode: v })}>
                      <SelectTrigger data-testid="split-online-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="razorpay">Razorpay</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Online Reference</Label>
                    <Input value={pay.online_reference}
                      onChange={(e) => setPay({ ...pay, online_reference: e.target.value })}
                      placeholder="UPI ref / txn id (for online portion)"
                      data-testid="split-online-reference" />
                  </div>
                  <div className="p-3 rounded-md border border-primary/30 bg-primary/5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total to collect:</span>
                      <span className="font-mono font-semibold" data-testid="split-total">
                        ₹{(Number(pay.cash_amount||0) + Number(pay.online_amount||0)).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                      <span>Outstanding:</span>
                      <span className="font-mono">₹{(Number(payFor.amount) - Number(payFor.paid_amount||0)).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </>
              )}
              <DialogFooter>
                <Button type="submit" data-testid="payment-save">Save Payment</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* PAYMENT HISTORY DIALOG */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Payment History {historyData?.invoice_no && <span className="font-mono text-base ml-2">{historyData.invoice_no}</span>}
            </DialogTitle>
          </DialogHeader>
          {historyLoading && <div className="text-center py-6 text-muted-foreground">Loading…</div>}
          {historyData && (
            <div className="space-y-6">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <SummaryTile label="Invoice Amount" value={`₹${Number(historyData.amount).toLocaleString("en-IN")}`} tid="hist-invoice-amount" />
                <SummaryTile label="Total Paid" value={`₹${Number(historyData.paid_amount).toLocaleString("en-IN")}`} tone="accent" tid="hist-total-paid" />
                <SummaryTile label="Remaining" value={`₹${Number(historyData.outstanding).toLocaleString("en-IN")}`} tone={historyData.outstanding > 0 ? "warn" : "muted"} tid="hist-outstanding" />
                <SummaryTile label="# Payments" value={historyData.payments_count} tid="hist-count" />
                <SummaryTile label="Last Payment" value={historyData.last_payment_at ? new Date(historyData.last_payment_at).toLocaleDateString() : "—"} tid="hist-last" />
                <SummaryTile label="Modes Used" value={historyData.modes_used.length ? historyData.modes_used.map((m) => m.replace("_", " ")).join(", ") : "—"} tid="hist-modes" />
              </div>

              {/* Resident context */}
              <div className="text-sm text-muted-foreground border border-border rounded-md p-3 bg-secondary/30">
                <span className="font-medium text-foreground">{historyData.resident?.name}</span>
                {" • "}Type: <span className="capitalize">{historyData.fee_type}</span>
                {historyData.period && <> {" • "}Period: {historyData.period}</>}
                {historyData.due_date && <> {" • "}Due: {historyData.due_date}</>}
                {" • "}<Badge variant={historyData.status === "paid" ? "default" : "secondary"} className="capitalize">{historyData.status}</Badge>
              </div>

              {/* v12 — Coverage window (start_date → end_date + duration) */}
              {(historyData.start_date || historyData.end_date) && (
                <div className="text-sm border border-primary/30 bg-primary/5 rounded-md p-3" data-testid="hist-coverage">
                  <div className="eyebrow text-muted-foreground">Subscription coverage</div>
                  <div className="mt-1 font-mono">
                    {historyData.start_date || "—"} → {historyData.end_date || "—"}
                    {historyData.duration_type && (
                      <span className="ml-2 text-muted-foreground">
                        ({historyData.duration_type === "custom"
                            ? `${historyData.duration_days} days`
                            : historyData.duration_type.replace(/([a-z]+)$/, " $1")})
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Amount and duration are independent — this fee's ₹{Number(historyData.amount).toLocaleString("en-IN")} covers the dates above.
                  </div>
                </div>
              )}

              {/* Payment ledger */}
              <div>
                <div className="eyebrow text-muted-foreground mb-2">Payment ledger</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="history-table">
                    <thead>
                      <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                        <th className="py-2 pr-3">Date</th>
                        <th className="py-2 pr-3">Mode</th>
                        <th className="py-2 pr-3">Book</th>
                        <th className="py-2 pr-3">Reference</th>
                        <th className="py-2 pr-3 text-right">Amount</th>
                        <th className="py-2 pr-3">Receipt</th>
                        <th className="py-2 pr-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.payments.map((p) => (
                        <tr key={p.id} className={`border-b border-border/40 ${p.reversed ? "opacity-50" : ""}`}
                            data-testid={`history-row-${p.id}`}>
                          <td className="py-2 pr-3 font-mono text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                          <td className="py-2 pr-3 uppercase text-xs">{p.mode?.replace("_", " ")}</td>
                          <td className="py-2 pr-3 text-xs text-muted-foreground">{(p.book || "—").replace("_", " ")}</td>
                          <td className="py-2 pr-3 font-mono text-xs text-muted-foreground truncate max-w-[160px]">{p.reference || "—"}</td>
                          <td className="py-2 pr-3 text-right font-mono font-semibold">
                            ₹{Number(p.amount).toLocaleString("en-IN")}
                            {p.reversed && <div className="text-[10px] text-destructive font-normal">REFUNDED</div>}
                          </td>
                          <td className="py-2 pr-3 font-mono text-[10px] text-muted-foreground">{p.receipt_no}</td>
                          <td className="py-2 pr-3 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="outline" onClick={() => downloadReceipt(p.id)}
                                      title="Download">
                                <Download className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => printReceipt(p.id)} title="Print">
                                <Printer className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => shareOnWhatsApp(p)}
                                      title="WhatsApp" className="text-green-700">
                                <MessageCircle className="w-3 h-3" />
                              </Button>
                              {(user?.role === "admin" || user?.role === "accountant") && !p.reversed && (
                                <Button size="sm" variant="outline" onClick={() => refundPayment(p.id, historyData.invoice_no)}
                                        title="Refund" data-testid={`refund-${p.id}`}
                                        className="text-destructive hover:text-destructive">
                                  <RotateCcw className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {historyData.payments.length === 0 && (
                        <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No payments recorded yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SummaryTile = ({ label, value, tone, tid }) => (
  <div className={`p-3 rounded-md border border-border ${
    tone === "warn" ? "bg-destructive/5" : tone === "accent" ? "bg-primary/5" : tone === "muted" ? "bg-secondary/40" : "bg-white"
  }`} data-testid={tid}>
    <div className="eyebrow text-muted-foreground">{label}</div>
    <div className="font-display font-bold text-xl mt-1 truncate">{value}</div>
  </div>
);
